import { Component, Input, OnInit } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { StripeCardService, StripePaymentMethodSummary } from 'src/app/_services/stripe-card.service';
import { StripeCardSavedEvent } from './stripe-add-card.component';

/** Self-contained "Payment methods" manager — lists every card on file for the business
 *  (`StripeCardService.listPaymentMethods()`), and lets the merchant set a different default or
 *  remove a card, plus add a new one via `app-stripe-add-card-button`. Owns its own list/loading
 *  state, so it can be dropped into any template (`<app-stripe-payment-methods>`) with no wiring. */
@Component({
  selector: 'app-stripe-payment-methods',
  templateUrl: './stripe-payment-methods.component.html',
  styleUrls: ['./stripe-payment-methods.component.scss']
})
export class StripePaymentMethodsComponent implements OnInit {
  /** Hides the built-in "Payment methods" title + Add-card button so a consuming page can supply
   *  its own card header/copy around this component (e.g. Subscription Management's summary
   *  cards) without rendering two headers stacked on top of each other. */
  @Input() showHeader = true;

  cards: StripePaymentMethodSummary[] = [];
  isLoading = true;
  loadError = '';
  pendingActionId: string | null = null;

  constructor(
    private readonly stripeCardService: StripeCardService,
    private readonly connMessageService: ConnMessageService,
    private readonly confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadCards();
  }

  loadCards(): void {
    this.isLoading = true;
    this.loadError = '';
    this.stripeCardService.listPaymentMethods().subscribe({
      next: (result) => {
        this.isLoading = false;
        if (!result.success) {
          this.loadError = result.message || 'Unable to load saved cards.';
          return;
        }
        this.cards = result.data;
      },
      error: () => {
        this.isLoading = false;
        this.loadError = 'Unable to load saved cards.';
      }
    });
  }

  brandLabel(brand: string): string {
    return brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';
  }

  expiryLabel(card: StripePaymentMethodSummary): string {
    if (!card.cardExpMonth || !card.cardExpYear) return '';
    return `${String(card.cardExpMonth).padStart(2, '0')}/${String(card.cardExpYear).slice(-2)}`;
  }

  setDefault(card: StripePaymentMethodSummary): void {
    if (card.isDefault || this.pendingActionId) return;
    this.pendingActionId = card.paymentMethodId;
    this.stripeCardService.setDefaultPaymentMethod(card.paymentMethodId).subscribe({
      next: (result) => {
        this.pendingActionId = null;
        if (!result.success) {
          this.connMessageService.showToastMessage(result.message || 'Failed to set default card.', 'error');
          return;
        }
        this.connMessageService.showToastMessage('Default card updated.', 'success');
        this.loadCards();
      },
      error: () => {
        this.pendingActionId = null;
        this.connMessageService.showToastMessage('Failed to set default card.', 'error');
      }
    });
  }

  confirmRemove(card: StripePaymentMethodSummary): void {
    if (this.pendingActionId) return;
    this.confirmationService.confirm({
      header: 'Remove card',
      message: `Remove the ${this.brandLabel(card.cardBrand)} card ending in ${card.cardLast4}?`,
      accept: () => this.removeCard(card)
    });
  }

  private removeCard(card: StripePaymentMethodSummary): void {
    this.pendingActionId = card.paymentMethodId;
    this.stripeCardService.removePaymentMethod(card.paymentMethodId).subscribe({
      next: (result) => {
        this.pendingActionId = null;
        if (!result.success) {
          // Covers the backend's "this is the default card billing an active subscription" case —
          // surface its message rather than a generic one, it already explains what to do.
          this.connMessageService.showToastMessage(result.message || 'Failed to remove card.', 'error');
          return;
        }
        this.connMessageService.showToastMessage('Card removed.', 'success');
        this.loadCards();
      },
      error: () => {
        this.pendingActionId = null;
        this.connMessageService.showToastMessage('Failed to remove card.', 'error');
      }
    });
  }

  onCardAdded(_event: StripeCardSavedEvent): void {
    // attachPaymentMethod() always appends the new card as the default, so a full reload keeps
    // isDefault flags correct across the whole list rather than just prepending locally.
    this.loadCards();
  }
}
